import {
  CANVAS_FLOOR_SECONDS,
  MAX_CANVAS_SECONDS,
  PACKED_CANVAS_MAX_SECONDS,
} from "./constants.ts";

export const GENERATION_SETTINGS_CONTRACT_VERSION = 1 as const;

export const GENERATION_SWAP_MODES = ["face", "person"] as const;
export const GENERATION_BLUR_SCOPES = ["eyes", "face", "head"] as const;
export const GENERATION_BLUR_FALLBACKS = ["widen", "fail"] as const;
export const GENERATION_LOOK_MODES = ["matched", "codec", "clean"] as const;
export const GENERATION_LOOP_MODES = [
  "auto",
  "full",
  "cut",
  "crossfade",
  "pingpong",
  "none",
] as const;

export interface GenerationSettings {
  swapMode: (typeof GENERATION_SWAP_MODES)[number];
  direction: string;
  performance: string;
  promptOverride: string;
  allowFaceBlurRetry: boolean;
  blurScope: (typeof GENERATION_BLUR_SCOPES)[number];
  blurFallback: (typeof GENERATION_BLUR_FALLBACKS)[number];
  blurFirst: boolean;
  escalateBlur: boolean;
  previs: boolean;
  canvasFloorSeconds: number;
  packCanvas: boolean;
  detailStrength: number;
  quantise: boolean;
  lookMode: (typeof GENERATION_LOOK_MODES)[number];
  loopMode: (typeof GENERATION_LOOP_MODES)[number];
  gifWidth: number;
  gifFps: number;
  maxMegabytes: number;
  trimLoop: boolean;
}

interface GenerationSettingsFieldBase {
  name: keyof GenerationSettings;
  label: string;
  description: string;
}

export type GenerationSettingsField =
  | (GenerationSettingsFieldBase & { control: "boolean" })
  | (GenerationSettingsFieldBase & {
      control: "select";
      options: readonly string[];
    })
  | (GenerationSettingsFieldBase & {
      control: "number";
      min: number;
      max: number;
      step: number;
    })
  | (GenerationSettingsFieldBase & {
      control: "text";
      maxLength: number;
      multiline: boolean;
    });

const DIRECTION_MAX_LENGTH = 2_000;
const PERFORMANCE_MAX_LENGTH = 2_000;
const PROMPT_OVERRIDE_MAX_LENGTH = 8_000;

export const GENERATION_SETTINGS_FIELDS: readonly GenerationSettingsField[] = [
  {
    name: "swapMode",
    label: "Identity replacement",
    description: "Replace the face only or the complete person.",
    control: "select",
    options: GENERATION_SWAP_MODES,
  },
  {
    name: "direction",
    label: "Additional direction",
    description: "Appended to the built prompt.",
    control: "text",
    maxLength: DIRECTION_MAX_LENGTH,
    multiline: true,
  },
  {
    name: "performance",
    label: "Performance notes",
    description: "Acting notes appended before measured source performance.",
    control: "text",
    maxLength: PERFORMANCE_MAX_LENGTH,
    multiline: true,
  },
  {
    name: "promptOverride",
    label: "Prompt override",
    description: "Replaces the complete built prompt, including measured notes.",
    control: "text",
    maxLength: PROMPT_OVERRIDE_MAX_LENGTH,
    multiline: true,
  },
  {
    name: "allowFaceBlurRetry",
    label: "Blur after likeness refusal",
    description: "Permit the verified blur path after a pre-generation likeness refusal.",
    control: "boolean",
  },
  {
    name: "blurScope",
    label: "Starting blur scope",
    description: "Select where the bounded blur ladder begins.",
    control: "select",
    options: GENERATION_BLUR_SCOPES,
  },
  {
    name: "blurFallback",
    label: "Missing-frame posture",
    description: "Widen across missed detections or fail before submission.",
    control: "select",
    options: GENERATION_BLUR_FALLBACKS,
  },
  {
    name: "blurFirst",
    label: "Blur before first submission",
    description: "Use verified blur and previs without first testing the original likeness.",
    control: "boolean",
  },
  {
    name: "escalateBlur",
    label: "Escalate blur ladder",
    description: "Climb bounded blur scopes while Gemini refuses before generation.",
    control: "boolean",
  },
  {
    name: "previs",
    label: "Previsualise performance",
    description: "Draw measured facial acting inside the verified blur.",
    control: "boolean",
  },
  {
    name: "canvasFloorSeconds",
    label: "Canvas floor",
    description: "Loop a shorter source up to this many seconds. 0 submits it at its own length.",
    control: "number",
    min: 0,
    max: MAX_CANVAS_SECONDS,
    step: 0.5,
  },
  {
    name: "packCanvas",
    label: "Pack the model canvas",
    description: "Send each source frame once at the model cadence, compressing the timeline.",
    control: "boolean",
  },
  {
    name: "detailStrength",
    label: "Detail strength",
    description: "Scale the source-matched detail bottleneck.",
    control: "number",
    min: 0.25,
    max: 2,
    step: 0.05,
  },
  {
    name: "quantise",
    label: "Quantise palette",
    description: "Match the source palette inside the detail bottleneck.",
    control: "boolean",
  },
  {
    name: "lookMode",
    label: "Look treatment",
    description: "Match, clean, or retain codec character from the source.",
    control: "select",
    options: GENERATION_LOOK_MODES,
  },
  {
    name: "loopMode",
    label: "Loop treatment",
    description: "Choose how the finished GIF joins its final frame to its first.",
    control: "select",
    options: GENERATION_LOOP_MODES,
  },
  {
    name: "gifWidth",
    label: "GIF width",
    description: "Starting width before the size-fitting ladder.",
    control: "number",
    min: 200,
    max: 1_280,
    step: 1,
  },
  {
    name: "gifFps",
    label: "GIF frame rate",
    description: "Use 0 for source cadence or choose an explicit rate.",
    control: "number",
    min: 0,
    max: 60,
    step: 1,
  },
  {
    name: "maxMegabytes",
    label: "Maximum GIF size",
    description: "Target size for the width and palette fitting ladder.",
    control: "number",
    min: 0.1,
    max: 25,
    step: 0.1,
  },
  {
    name: "trimLoop",
    label: "Trim to one source beat",
    description: "Remove provider loop padding before detail and GIF encoding.",
    control: "boolean",
  },
] as const;

const DEFAULT_GENERATION_SETTINGS: GenerationSettings = {
  swapMode: "face",
  direction: "",
  performance: "",
  promptOverride: "",
  allowFaceBlurRetry: true,
  blurScope: "face",
  blurFallback: "widen",
  blurFirst: true,
  escalateBlur: true,
  previs: true,
  canvasFloorSeconds: CANVAS_FLOOR_SECONDS,
  packCanvas: false,
  detailStrength: 1.4,
  quantise: true,
  lookMode: "matched",
  loopMode: "full",
  gifWidth: 480,
  gifFps: 0,
  maxMegabytes: 3,
  trimLoop: true,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function booleanValue(value: unknown, name: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${name} must be a boolean`);
  return value;
}

function enumValue<const T extends readonly string[]>(
  value: unknown,
  options: T,
  name: string,
): T[number] {
  if (typeof value !== "string" || !options.includes(value)) {
    throw new Error(`${name} must be one of: ${options.join(", ")}`);
  }
  return value as T[number];
}

function textValue(value: unknown, maxLength: number, name: string): string {
  if (typeof value !== "string") throw new Error(`${name} must be text`);
  const normalized = value.trim();
  if (normalized.length > maxLength) {
    throw new Error(`${name} must be at most ${maxLength} characters`);
  }
  return normalized;
}

function numberValue(
  value: unknown,
  options: { min: number; max: number; integer?: boolean; zeroOrAtLeastOne?: boolean },
  name: string,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${name} must be a finite number`);
  }
  if (options.integer && !Number.isInteger(value)) throw new Error(`${name} must be an integer`);
  if (
    value < options.min ||
    value > options.max ||
    (options.zeroOrAtLeastOne && value > 0 && value < 1)
  ) {
    throw new Error(`${name} must be between ${options.min} and ${options.max}`);
  }
  return value;
}

export function defaultGenerationSettings(): GenerationSettings {
  return { ...DEFAULT_GENERATION_SETTINGS };
}

export function normalizeGenerationSettings(value: unknown): GenerationSettings {
  if (value === undefined) return defaultGenerationSettings();
  if (!isRecord(value)) throw new Error("generation settings must be an object");
  const known = new Set(GENERATION_SETTINGS_FIELDS.map(({ name }) => name));
  for (const key of Object.keys(value)) {
    if (!known.has(key as keyof GenerationSettings)) {
      throw new Error(`unknown generation setting: ${key}`);
    }
  }

  const result = defaultGenerationSettings();
  if (value.swapMode !== undefined) {
    result.swapMode = enumValue(value.swapMode, GENERATION_SWAP_MODES, "swapMode");
  }
  if (value.direction !== undefined) {
    result.direction = textValue(value.direction, DIRECTION_MAX_LENGTH, "direction");
  }
  if (value.performance !== undefined) {
    result.performance = textValue(value.performance, PERFORMANCE_MAX_LENGTH, "performance");
  }
  if (value.promptOverride !== undefined) {
    result.promptOverride = textValue(
      value.promptOverride,
      PROMPT_OVERRIDE_MAX_LENGTH,
      "promptOverride",
    );
  }
  if (value.allowFaceBlurRetry !== undefined) {
    result.allowFaceBlurRetry = booleanValue(value.allowFaceBlurRetry, "allowFaceBlurRetry");
  }
  if (value.blurScope !== undefined) {
    result.blurScope = enumValue(value.blurScope, GENERATION_BLUR_SCOPES, "blurScope");
  }
  if (value.blurFallback !== undefined) {
    result.blurFallback = enumValue(value.blurFallback, GENERATION_BLUR_FALLBACKS, "blurFallback");
  }
  if (value.blurFirst !== undefined) result.blurFirst = booleanValue(value.blurFirst, "blurFirst");
  if (value.escalateBlur !== undefined) {
    result.escalateBlur = booleanValue(value.escalateBlur, "escalateBlur");
  }
  if (value.previs !== undefined) result.previs = booleanValue(value.previs, "previs");
  if (value.canvasFloorSeconds !== undefined) {
    result.canvasFloorSeconds = numberValue(
      value.canvasFloorSeconds,
      { min: 0, max: MAX_CANVAS_SECONDS },
      "canvasFloorSeconds",
    );
  }
  if (value.packCanvas !== undefined) {
    result.packCanvas = booleanValue(value.packCanvas, "packCanvas");
  }
  if (value.detailStrength !== undefined) {
    result.detailStrength = numberValue(
      value.detailStrength,
      { min: 0.25, max: 2 },
      "detailStrength",
    );
  }
  if (value.quantise !== undefined) result.quantise = booleanValue(value.quantise, "quantise");
  if (value.lookMode !== undefined) {
    result.lookMode = enumValue(value.lookMode, GENERATION_LOOK_MODES, "lookMode");
  }
  if (value.loopMode !== undefined) {
    result.loopMode = enumValue(value.loopMode, GENERATION_LOOP_MODES, "loopMode");
  }
  if (value.gifWidth !== undefined) {
    result.gifWidth = numberValue(
      value.gifWidth,
      { min: 200, max: 1_280, integer: true },
      "gifWidth",
    );
  }
  if (value.gifFps !== undefined) {
    result.gifFps = numberValue(
      value.gifFps,
      { min: 0, max: 60, zeroOrAtLeastOne: true },
      "gifFps",
    );
  }
  if (value.maxMegabytes !== undefined) {
    result.maxMegabytes = numberValue(value.maxMegabytes, { min: 0.1, max: 25 }, "maxMegabytes");
  }
  if (value.trimLoop !== undefined) result.trimLoop = booleanValue(value.trimLoop, "trimLoop");

  // A packed canvas can never exceed PACKED_CANVAS_MAX_SECONDS, so a floor at
  // or above that loops EVERY packed run straight back up: full floor price,
  // and fewer frames than not packing at all. A lower floor composes fine —
  // it simply sets a minimum the packed canvas is padded to — so only the
  // self-defeating case is refused.
  if (result.packCanvas && result.canvasFloorSeconds >= PACKED_CANVAS_MAX_SECONDS) {
    throw new Error(
      `packCanvas needs canvasFloorSeconds below ${PACKED_CANVAS_MAX_SECONDS}: ` +
        "a packed canvas would be looped back up to the floor",
    );
  }
  if (!result.previs && (result.blurFirst || result.allowFaceBlurRetry)) {
    throw new Error("previs may be disabled only when blurFirst and allowFaceBlurRetry are false");
  }
  return result;
}

function orderedGenerationSettings(settings: GenerationSettings): GenerationSettings {
  const normalized = normalizeGenerationSettings(settings);
  return Object.fromEntries(
    GENERATION_SETTINGS_FIELDS.map(({ name }) => [name, normalized[name]]),
  ) as unknown as GenerationSettings;
}

export function canonicalGenerationSettings(settings: GenerationSettings): string {
  return JSON.stringify({
    version: GENERATION_SETTINGS_CONTRACT_VERSION,
    values: orderedGenerationSettings(settings),
  });
}

export function parseGenerationSettingsJson(payload: string): GenerationSettings {
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    throw new Error("generation settings payload must be valid JSON");
  }
  if (!isRecord(parsed)) throw new Error("generation settings payload must be an object");
  const keys = Object.keys(parsed);
  if (keys.some((key) => key !== "version" && key !== "values")) {
    throw new Error("generation settings payload has unknown fields");
  }
  if (parsed.version !== GENERATION_SETTINGS_CONTRACT_VERSION) {
    throw new Error("unsupported generation settings contract version");
  }
  if (!isRecord(parsed.values))
    throw new Error("generation settings payload values must be an object");
  return normalizeGenerationSettings(parsed.values);
}
